import * as React from "react";
import { Provider, observer, inject } from "mobx-react";
import { HashRouter as Router, Route, Switch, withRouter } from "react-router-dom";
import { RouteChildrenProps, Redirect } from "react-router";

import { Login } from "./components/auth/Login";
import { ApplicationTemplate } from "./components/ApplicationTemplate";
import CustomerList from "./components/customer/CustomerList";
import CustomerProfile from "./components/customer/CustomerProfile";
import HorsesList from "./components/horses/HorseList";
import { HorseProfile } from "./components/horses/HorseProfile";
import HorsesForSaleList from "./components/horses-for-sale/HorsesForSaleList";
import { HorsesForSaleProfile } from "./components/horses-for-sale/HorsesForSaleProfile";
import { MapAddresses } from "./components/horses-for-sale/MapAddresses";
import { AssignDayForSaleAndGenerateHipNumbers } from "./components/horses-for-sale/AssignDayForSaleAndGenerateHipNumbers";
import { PreFilledForms } from "./components/horses-for-sale/PreFilledForms";
import { AssignSpotAgriplex } from "./components/horses-for-sale/AssignSpotAgriplex";
import { WinningBidders } from "./components/horses-for-sale/WinningBiddersPictures";
import ReportsTemplate from "./components/reports/ReportsTemplate";
import CheckoutTemplate from "./components/checkout/CheckoutTemplate";
import ScrollToTop from "./components/common/ScrollToTop";
import { EntryFeeInvoiceGeneration } from "./components/horses-for-sale/EntryFeeInvoiceGeneration";
import GenerateInterestInvoices from "./components/customer/GenerateInterestInvoices";

import StoreBuilder from "./stores/StoreBuilder";
import { SessionStore } from "./stores/SessionStore";

import "bootstrap/dist/css/bootstrap.css";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-datetime/css/react-datetime.css";

const stores = StoreBuilder();
const RedirectToCustomerList = () => <Redirect to="/customers" />;

const CustomerListWrapper = (props: RouteChildrenProps) => {
    return <CustomerList />;
};

const CheckoutTemplateWrapper = (props: RouteChildrenProps<{ currentTab: string | undefined; horseForSaleIds: string | undefined }>) => {
    if (!props.match) {
        console.error("Could not find props.match in CheckoutTemplateWrapper");
        return <div />;
    }

    const horseForSaleIds = (props.match.params.horseForSaleIds || "").split("+").map(id => parseInt(id, 10));
    const currentTab = props.match.params.currentTab || "buyers";
    return <CheckoutTemplate currentTab={currentTab} horseForSaleIds={horseForSaleIds} />;
};

const CustomerProfileWrapper = (props: RouteChildrenProps<{ id: string | undefined; currentTab: string | undefined }>) => {
    if (!props.match) {
        console.error("Could not find props.match in CustomerProfileWrapper");
        return <div />;
    }

    const id = parseInt("" + props.match.params.id, 10);
    stores.customerStore.updateSubjectId(id);

    const currentTab = props.match.params.currentTab || "overview";

    return <CustomerProfile key={id} currentTab={currentTab} />;
};

const HorsesListWrapper = (props: RouteChildrenProps) => {
    return <HorsesList />;
};
const HorsesProfileWrapper = (props: RouteChildrenProps<{ id: string | undefined }>) => {
    if (!props.match) {
        console.error("Could not find props.match in HorsesProfileWrapper");
        return <div />;
    }
    const id = parseInt("" + props.match.params.id, 10);
    stores.horseStore.updateSubjectId(id);

    return <HorseProfile key={id} />;
};

const HorsesForSaleListWrapper = (props: RouteChildrenProps) => {
    return <HorsesForSaleList />;
};
const HorsesForSaleProfileWrapper = (props: RouteChildrenProps<{ id: string | undefined; currentTab: string | undefined }>) => {
    if (!props.match) {
        console.error("Could not find props.match in HorsesForSaleProfileWrapper");
        return <div />;
    }
    const currentTab = props.match.params.currentTab || "application";
    const id = parseInt("" + props.match.params.id, 10);
    stores.horseForSaleStore.updateSubjectId(id);

    return <HorsesForSaleProfile key={id} currentTab={currentTab} />;
};

const ReportsWrapper = (props: RouteChildrenProps<{ currentTab: string | undefined }>) => {
    if (!props.match) {
        console.error("Could not find props.match in HorsesForSaleProfileWrapper");
        return <div />;
    }
    const currentTab = props.match.params.currentTab || "customer-emails";
    return <ReportsTemplate currentTab={currentTab} />;
};
const MapsAddressesWrapper = (props: RouteChildrenProps) => {
    return <MapAddresses />;
};
const PreFilledFormsWrapper = (props: RouteChildrenProps) => {
    return <PreFilledForms />;
};
const AssignDayForSaleAndGenerateHipNumbersWrapper = (props: RouteChildrenProps) => {
    return <AssignDayForSaleAndGenerateHipNumbers />;
};
const AssignSpotAgriplexWrapper = (props: RouteChildrenProps) => {
    return <AssignSpotAgriplex />;
};
const WinningBiddersWrapper = (props: RouteChildrenProps) => {
    return <WinningBidders />;
};
const EntryFeeInvoiceWrapper = () => {
    return <EntryFeeInvoiceGeneration />;
};

const GenerateInterestInvoicesWrapper = () => {
    return <GenerateInterestInvoices />;
};

interface Props {
    sessionStore?: SessionStore;
}

@inject("sessionStore")
@observer
class Application extends React.Component<Props, {}> {
    render() {
        const widget = this.props.sessionStore!.loggedIn ? this.loggedIn : this.login;
        return (
            <Router>
                <Route render={({ location }) => React.createElement(widget, { location })} />
            </Router>
        );
    }

    private loggedIn(props: any) {
        return (
            <ApplicationTemplate>
                {/* <TransitionGroup>
                    <CSSTransition 
                        key={props.location.pathname} 
                        in={props.match != null}
                        timeout={300}
                        classNames="page"
                        unmountOnExit={true}
                    > */}
                <ScrollToTop>
                    <Switch>
                        <Route exact={true} path="/" component={RedirectToCustomerList} />
                        <Route exact={true} path="/customers" component={CustomerListWrapper} />
                        <Route exact={true} path="/customer/:id/:currentTab" component={CustomerProfileWrapper} />
                        <Route exact={true} path="/generate-interest-invoices" component={GenerateInterestInvoicesWrapper} />
                        <Route exact={true} path="/horses" component={HorsesListWrapper} />
                        <Route exact={true} path="/horse/:id" component={HorsesProfileWrapper} />
                        <Route exact={true} path="/horses-for-sale" component={HorsesForSaleListWrapper} />
                        <Route exact={true} path="/horses-for-sale/:id/:currentTab" component={HorsesForSaleProfileWrapper} />
                        <Route exact={true} path="/reports/:currentTab" component={ReportsWrapper} />
                        <Route exact={true} path="/horses-for-sale/address-map" component={MapsAddressesWrapper} />
                        <Route exact={true} path="/horses-for-sale/pre-filled-forms" component={PreFilledFormsWrapper} />
                        <Route exact={true} path="/horses-for-sale/generate-entry-fee-invoices" component={EntryFeeInvoiceWrapper} />
                        <Route
                            exact={true}
                            path="/horses-for-sale/assign-day-and-generate-hip-number"
                            component={AssignDayForSaleAndGenerateHipNumbersWrapper}
                        />
                        <Route exact={true} path="/horses-for-sale/assign-spot-agripex" component={AssignSpotAgriplexWrapper} />
                        <Route exact={true} path="/winning-bidders" component={WinningBiddersWrapper} />
                        <Route exact={true} path="/checkout/:horseForSaleIds/:currentTab" component={CheckoutTemplateWrapper} />
                        <Route exact={true} path="/horses-for-sale/winning-bidders-pictures" component={WinningBiddersWrapper} />
                    </Switch>
                </ScrollToTop>
                {/* </CSSTransition>
                </TransitionGroup> */}
            </ApplicationTemplate>
        );
    }

    private login() {
        return <Login />;
    }
}

export default () => {
    return (
        <Provider {...stores}>
            <Application />
        </Provider>
    );
};



// WEBPACK FOOTER //
// ./src/Application.tsx